/**
 * Friendly empty-state used wherever a list / page has nothing to show yet.
 * A coloured icon chip + title + (optional) description + (optional) action.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Tint = 'blue' | 'coral' | 'green' | 'amber' | 'slate';

const TINT_CLASS: Record<Tint, string> = {
  blue: 'bg-[#4f6bff]/10 text-[#4f6bff]',
  coral: 'bg-[#ff7a59]/12 text-[#ff7a59]',
  green: 'bg-[#22c55e]/12 text-[#16a34a]',
  amber: 'bg-[#ffc542]/20 text-[#d39400]',
  slate: 'bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-secondary)]',
};

export function EmptyState({
  icon: Icon,
  tint = 'slate',
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  tint?: Tint;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-10 text-center', className)}>
      {Icon && (
        <span className={cn('mb-4 grid size-14 place-items-center rounded-2xl', TINT_CLASS[tint])}>
          <Icon className="size-7" />
        </span>
      )}
      <p className="text-base font-semibold text-[color:var(--kz-color-ink-primary)]">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-[color:var(--kz-color-ink-secondary)]">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
